// =============================================
// RedirectProposalsTable — UI dla Redirect Mode (STOP-5)
//
// Renderowane TYLKO gdy goalMode='redirect' w ExistingPostsPanel.
// Skanuje propozycje (Quick L1+L2 free / Deep +L3 LLM ~$0.0025/node) -> tabela
// z dropdown wyboru wp_post per node + button "Apply pending" -> INSERT wp_redirects
// z status='pending'.
// =============================================

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, Send, Trash2, Zap, ExternalLink, RefreshCw } from "lucide-react";
import { useRedirectProposals, type MatchProposal } from "@/hooks/useRedirectProposals";

// FIX 2026-06-01: props mapId/websiteId zamiast `map: ThematicMap`. Wczesniej
// `map.id` mogl byc undefined (SELECT w ExistingPostsPanel useQuery nie zawieral
// kolumny 'id') -> Supabase eq('map_id', 'undefined') -> Postgres UUID syntax error.
interface RedirectProposalsTableProps {
  mapId: string;
  websiteId: string | null;
}

function confidenceColor(c: number): string {
  if (c >= 0.7) return "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/40";
  if (c >= 0.4) return "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40";
  return "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/40";
}

export function RedirectProposalsTable({ mapId, websiteId }: RedirectProposalsTableProps) {
  // Guard: nie mount'uj jesli brak mapId (parent powinien gate'owac, ale extra safety).
  if (!mapId) return null;

  const {
    scanning,
    scanMode,
    results,
    totalLlmCostUsd,
    scanDurationMs,
    pendingRedirects,
    loadingPending,
    eligibleNodes,
    loadingNodes,
    scanAllNodes,
    applyPending,
    deletePending,
    refresh,
  } = useRedirectProposals(mapId, websiteId);

  // Per node: wybór propozycji (wpPostId)
  const [selectedProposals, setSelectedProposals] = useState<Map<string, MatchProposal>>(
    new Map(),
  );
  const [showDeepConfirm, setShowDeepConfirm] = useState(false);

  const eligibleNodeIds = useMemo(
    () => eligibleNodes.map((n) => n.id),
    [eligibleNodes],
  );

  const estimatedDeepCostUsd = eligibleNodeIds.length * 0.0025;

  const handleScan = async (mode: "quick" | "deep") => {
    if (mode === "deep") {
      setShowDeepConfirm(true);
      return;
    }
    await scanAllNodes(eligibleNodeIds, mode);
    setSelectedProposals(new Map());
  };

  const handleDeepConfirm = async () => {
    setShowDeepConfirm(false);
    await scanAllNodes(eligibleNodeIds, "deep");
    setSelectedProposals(new Map());
  };

  const handleSelectProposal = (nodeId: string, proposal: MatchProposal) => {
    setSelectedProposals((prev) => {
      const next = new Map(prev);
      next.set(nodeId, proposal);
      return next;
    });
  };

  const handleApply = async (nodeId: string) => {
    const proposal = selectedProposals.get(nodeId) ?? results.find((r) => r.nodeId === nodeId)?.proposals[0];
    const node = eligibleNodes.find((n) => n.id === nodeId);
    if (!proposal || !node) return;
    if (!proposal.wpUrl) return; // brak source URL — nie da się
    const ok = await applyPending({
      fromUrl: proposal.wpUrl,
      fromWpPostId: proposal.wpPostId,
      toNodeId: nodeId,
      toSlugPlanned: node.slug,
      matchConfidence: proposal.confidence,
      matchMethod: proposal.method,
      reason: proposal.reason,
    });
    if (ok) {
      setSelectedProposals((prev) => {
        const next = new Map(prev);
        next.delete(nodeId);
        return next;
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-base">
              🔗 Propozycje redirectów (Redirect Mode)
            </CardTitle>
            <CardDescription>
              Skanuj podobieństwo nodów ↔ wpisów WP, zatwierdź pary jako pending redirect.
              <span className="block text-[11px] mt-1">
                Status <code className="font-mono">pending</code> = czeka na publikację noda (target).
                Fizyczny 301 do WP w STOP-6 (out of MVP — manual w plugin Redirection).
              </span>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toolbar Quick/Deep + cost forensics */}
        <div className="flex flex-wrap items-center gap-2 border rounded-md p-3 bg-muted/20">
          <Button
            onClick={() => handleScan("quick")}
            disabled={scanning || eligibleNodeIds.length === 0}
            size="sm"
            className="bg-amber-500/15 hover:bg-amber-500/25 text-amber-700 dark:text-amber-300 border border-amber-500/40"
            title="L1 hash + L2 containment — deterministic, free, <1s/node"
          >
            {scanning && scanMode === "quick" ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Search className="mr-1.5 h-3.5 w-3.5" />
            )}
            Skanuj Quick (free)
          </Button>
          <Button
            onClick={() => handleScan("deep")}
            disabled={scanning || eligibleNodeIds.length === 0}
            size="sm"
            variant="outline"
            className="border-purple-500/40 text-purple-700 dark:text-purple-300 hover:bg-purple-500/10"
            title={`Quick + L3 LLM weryfikacja weak matches (~$0.0025/node × ${eligibleNodeIds.length} = $${estimatedDeepCostUsd.toFixed(4)})`}
          >
            {scanning && scanMode === "deep" ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Zap className="mr-1.5 h-3.5 w-3.5" />
            )}
            Skanuj Deep (LLM)
          </Button>
          <Button onClick={refresh} disabled={loadingPending} size="sm" variant="ghost" className="ml-auto">
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loadingPending ? "animate-spin" : ""}`} />
            Odśwież pending
          </Button>
        </div>

        {/* Forensics card po skanowaniu */}
        {results.length > 0 && (
          <div className="rounded-md border bg-card p-3 space-y-1 text-xs">
            <p className="font-semibold">Ostatni skan: {scanMode === "quick" ? "Quick (L1+L2)" : "Deep (L1+L2+L3 LLM)"}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 tabular-nums">
              <div>
                Nodów: <span className="font-mono">{results.length}</span>
              </div>
              <div>
                Z propozycjami: <span className="font-mono">{results.filter((r) => r.proposals.length > 0).length}</span>
              </div>
              <div>
                Koszt LLM: <span className="font-mono">${totalLlmCostUsd.toFixed(4)}</span>
              </div>
              <div>
                Duration: <span className="font-mono">{(scanDurationMs / 1000).toFixed(1)}s</span>
              </div>
            </div>
          </div>
        )}

        {/* Główna tabela propozycji per node */}
        {results.length > 0 && (
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Node (target)</TableHead>
                  <TableHead>Planowany URL</TableHead>
                  <TableHead>Propozycja WP post</TableHead>
                  <TableHead className="w-[100px]">Confidence</TableHead>
                  <TableHead className="w-[120px]">Method</TableHead>
                  <TableHead className="w-[100px]">Akcja</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((r) => {
                  const selected = selectedProposals.get(r.nodeId);
                  const hasPending = pendingRedirects.some(
                    (p) => p.to_node_id === r.nodeId && p.status === "pending",
                  );
                  if (r.proposals.length === 0) {
                    return (
                      <TableRow key={r.nodeId}>
                        <TableCell className="text-xs">{r.nodeTitle}</TableCell>
                        <TableCell className="text-xs font-mono">/{r.nodeSlug}</TableCell>
                        <TableCell colSpan={4} className="text-xs text-muted-foreground italic">
                          Brak propozycji (containment poniżej threshold 0.4)
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return (
                    <TableRow key={r.nodeId}>
                      <TableCell className="text-xs max-w-[200px] truncate">{r.nodeTitle}</TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">/{r.nodeSlug}</TableCell>
                      <TableCell>
                        <Select
                          value={selected?.wpPostId ?? r.proposals[0].wpPostId}
                          onValueChange={(v) => {
                            const prop = r.proposals.find((p) => p.wpPostId === v);
                            if (prop) handleSelectProposal(r.nodeId, prop);
                          }}
                          disabled={hasPending}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {r.proposals.map((p) => (
                              <SelectItem key={p.wpPostId} value={p.wpPostId} className="text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="truncate max-w-[280px]">{p.wpTitle}</span>
                                  <Badge variant="outline" className={`text-[9px] ${confidenceColor(p.confidence)}`}>
                                    {(p.confidence * 100).toFixed(0)}%
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const active = selected ?? r.proposals[0];
                          return (
                            <Badge variant="outline" className={`${confidenceColor(active.confidence)} text-[10px]`}>
                              {(active.confidence * 100).toFixed(0)}%
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] uppercase">
                          {(selected ?? r.proposals[0]).method}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {hasPending ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40">
                            ✓ pending
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleApply(r.nodeId)}
                            disabled={!(selected ?? r.proposals[0])?.wpUrl}
                            className="h-7 text-xs"
                          >
                            <Send className="mr-1 h-3 w-3" />
                            Apply
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pending redirects (z DB) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Pending redirects ({pendingRedirects.length})</h4>
            {pendingRedirects.length > 0 && (
              <p className="text-[11px] text-muted-foreground italic">
                Status pending = czeka na publikację noda. Fizyczny apply 301 do WP w STOP-6.
              </p>
            )}
          </div>
          {pendingRedirects.length === 0 ? (
            <div className="border border-dashed rounded-md p-6 text-center text-sm text-muted-foreground">
              Brak pending redirects. Skanuj propozycje i kliknij Apply żeby dodać.
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From URL</TableHead>
                    <TableHead>To slug / URL</TableHead>
                    <TableHead className="w-[80px]">Status</TableHead>
                    <TableHead className="w-[80px]">Confidence</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingRedirects.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-xs max-w-[250px] truncate">
                        <a href={p.from_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">
                          {p.from_url}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {p.to_wp_post_url ? (
                          <a href={p.to_wp_post_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                            {p.to_wp_post_url}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">
                            /{p.to_slug_planned} <span className="text-[10px]">(planowany)</span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            p.status === "pending"
                              ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40"
                              : p.status === "applied"
                              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {p.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {p.match_confidence != null ? (
                          <Badge variant="outline" className={`text-[10px] ${confidenceColor(p.match_confidence)}`}>
                            {(p.match_confidence * 100).toFixed(0)}%
                          </Badge>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deletePending(p.id)}
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500"
                          title="Usuń redirect"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Deep scan confirm dialog */}
        <AlertDialog open={showDeepConfirm} onOpenChange={setShowDeepConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deep scan (LLM weryfikacja)</AlertDialogTitle>
              <AlertDialogDescription>
                Quick scan (L1+L2) zawsze działa za darmo. Deep scan dodatkowo wywołuje LLM
                (gpt-4.1-mini) do weryfikacji weak matches (confidence 0.4-0.7). Estymowany koszt:
                <strong className="block mt-1">
                  {eligibleNodeIds.length} nodów × ~$0.0025 = ~${estimatedDeepCostUsd.toFixed(4)}
                </strong>
                <span className="block mt-2 text-xs">
                  Realny koszt zależy od liczby weak matches per node — zazwyczaj mniej niż estymata.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anuluj</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeepConfirm}>Uruchom Deep scan</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
